var __defProp = Object.defineProperty;
var __typeError = (msg) => {
  throw TypeError(msg);
};
var __defNormalProp = (obj, key, value) => key in obj ? __defProp(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __publicField = (obj, key, value) => __defNormalProp(obj, typeof key !== "symbol" ? key + "" : key, value);
var __accessCheck = (obj, member, msg) => member.has(obj) || __typeError("Cannot " + msg);
var __privateGet = (obj, member, getter) => (__accessCheck(obj, member, "read from private field"), getter ? getter.call(obj) : member.get(obj));
var __privateAdd = (obj, member, value) => member.has(obj) ? __typeError("Cannot add the same private member more than once") : member instanceof WeakSet ? member.add(obj) : member.set(obj, value);
var __privateSet = (obj, member, value, setter) => (__accessCheck(obj, member, "write to private field"), setter ? setter.call(obj, value) : member.set(obj, value), value);
var __privateMethod = (obj, member, method) => (__accessCheck(obj, member, "access private method"), method);
var _popoverTrigger, _onTriggerEnter, _onTriggerLeave, _onPopoverEnter, _onPopoverLeave, _updatePosition, _AnchoredPopoverComponent_instances, getVerticalPositionAnchor_fn, queryVerticalPositionAnchor_fn, isUsableExternalVerticalPositionAnchor_fn, _resizeListener;
import { Component } from "@theme/component";
import { debounce, requestIdleCallback } from "@theme/utilities";
class AnchoredPopoverComponent extends Component {
  constructor() {
    super(...arguments);
    __privateAdd(this, _AnchoredPopoverComponent_instances);
    __publicField(this, "requiredRefs", ["popover", "trigger"]);
    __publicField(this, "interaction_delay", 200);
    __privateAdd(
      this,
      _popoverTrigger,
      /** @type {number | null} */
      null
    );
    __privateAdd(this, _onTriggerEnter, () => {
      const { trigger, popover } = this.refs;
      trigger.dataset.hoverActive = "true";
      if (!popover.matches(":popover-open")) {
        __privateSet(this, _popoverTrigger, setTimeout(() => {
          if (trigger.matches("[data-hover-active]")) popover.showPopover();
        }, this.interaction_delay));
      }
    });
    __privateAdd(this, _onTriggerLeave, () => {
      const { trigger, popover } = this.refs;
      delete trigger.dataset.hoverActive;
      if (__privateGet(this, _popoverTrigger)) clearTimeout(__privateGet(this, _popoverTrigger));
      if (popover.matches(":popover-open")) {
        __privateSet(this, _popoverTrigger, setTimeout(() => {
          popover.hidePopover();
        }, this.interaction_delay));
      }
    });
    __privateAdd(this, _onPopoverEnter, () => {
      if (__privateGet(this, _popoverTrigger)) clearTimeout(__privateGet(this, _popoverTrigger));
    });
    __privateAdd(this, _onPopoverLeave, () => {
      const { popover } = this.refs;
      __privateSet(this, _popoverTrigger, setTimeout(() => {
        popover.hidePopover();
      }, this.interaction_delay));
    });
    /**
     * Updates the popover position by calculating trigger element bounds
     * and setting CSS custom properties on the popover element.
     */
    __privateAdd(this, _updatePosition, async () => {
      const { popover, trigger } = this.refs;
      if (!popover || !trigger) return;
      const triggerPositions = trigger.getBoundingClientRect();
      const verticalPositionAnchor = __privateMethod(this, _AnchoredPopoverComponent_instances, getVerticalPositionAnchor_fn).call(this);
      const verticalPositions = (verticalPositionAnchor != null ? verticalPositionAnchor : trigger).getBoundingClientRect();
      popover.style.setProperty("--anchor-top", "".concat(verticalPositions.top));
      popover.style.setProperty("--anchor-right", "".concat(window.innerWidth - triggerPositions.right));
      popover.style.setProperty("--anchor-bottom", "".concat(window.innerHeight - verticalPositions.bottom));
      popover.style.setProperty("--anchor-left", "".concat(triggerPositions.left));
      popover.style.setProperty("--anchor-height", "".concat(verticalPositions.height));
      popover.style.setProperty("--anchor-width", "".concat(triggerPositions.width));
    });
    /**
     * Debounced resize handler that optionally closes the popover
     * when the window is resized, based on the data-close-on-resize attribute.
     */
    __privateAdd(this, _resizeListener, debounce(() => {
      const popover = (
        /** @type {HTMLElement} */
        this.refs.popover
      );
      if (popover && popover.matches(":popover-open")) {
        popover.hidePopover();
      }
    }, 100));
  }
  /**
   * Opens a dialog from a disclosure trigger without toggling the hover popover preview.
   *
   * @param {string} dialogId - The ID of the dialog component to open.
   * @param {Event} event - The click or activation event.
   */
  openDialog(dialogId, event) {
    event.preventDefault();
    const { popover, trigger } = this.refs;
    const triggerAriaControls = trigger.getAttribute("aria-controls");
    if (__privateGet(this, _popoverTrigger) !== null) {
      clearTimeout(__privateGet(this, _popoverTrigger));
      __privateSet(this, _popoverTrigger, null);
    }
    delete trigger.dataset.hoverActive;
    if (popover.matches(":popover-open")) {
      popover.hidePopover();
    }
    const dialog = (
      /** @type {(HTMLElement & { showDialog?: () => void }) | null} */
      document.getElementById(dialogId)
    );
    if (typeof (dialog == null ? void 0 : dialog.showDialog) !== "function") return;
    dialog.addEventListener(
      "dialog:close",
      () => {
        const focusTarget = trigger.isConnected ? trigger : getTriggerByAriaControls(triggerAriaControls);
        focusTarget == null ? void 0 : focusTarget.focus();
      },
      { once: true }
    );
    dialog.showDialog();
  }
  /**
   * Component initialization - sets up event listeners for resize and popover toggle events.
   */
  connectedCallback() {
    super.connectedCallback();
    const { popover, trigger } = this.refs;
    if (this.dataset.closeOnResize) {
      popover.addEventListener("beforetoggle", (event) => {
        const evt = (
          /** @type {ToggleEvent} */
          event
        );
        window[evt.newState === "open" ? "addEventListener" : "removeEventListener"]("resize", __privateGet(this, _resizeListener));
      });
    }
    if (this.dataset.hoverTriggered) {
      trigger.addEventListener("pointerenter", __privateGet(this, _onTriggerEnter));
      trigger.addEventListener("pointerleave", __privateGet(this, _onTriggerLeave));
      popover.addEventListener("pointerenter", __privateGet(this, _onPopoverEnter));
      popover.addEventListener("pointerleave", __privateGet(this, _onPopoverLeave));
    }
    if (!CSS.supports("position-anchor: --trigger")) {
      popover.addEventListener("beforetoggle", () => {
        __privateGet(this, _updatePosition).call(this);
      });
      requestIdleCallback(() => {
        __privateGet(this, _updatePosition).call(this);
      });
    }
  }
  /**
   * Component cleanup - removes resize event listener.
   */
  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener("resize", __privateGet(this, _resizeListener));
  }
}
_popoverTrigger = new WeakMap();
_onTriggerEnter = new WeakMap();
_onTriggerLeave = new WeakMap();
_onPopoverEnter = new WeakMap();
_onPopoverLeave = new WeakMap();
_updatePosition = new WeakMap();
_AnchoredPopoverComponent_instances = new WeakSet();
/**
 * Finds the vertical fallback anchor. Internal refs are kept for existing callers;
 * data-vertical-position-anchor supports sibling/external anchors outside Component refs.
 *
 * @returns {HTMLElement | null}
 */
getVerticalPositionAnchor_fn = function() {
  var _a;
  const internalAnchor = this.refs.verticalPositionAnchor;
  if (internalAnchor instanceof HTMLElement) return internalAnchor;
  const anchorName = (_a = this.dataset.verticalPositionAnchor) == null ? void 0 : _a.trim();
  if (!anchorName) return null;
  const selector = '[data-anchor-name="'.concat(CSS.escape(anchorName), '"]');
  const scopedAnchor = __privateMethod(this, _AnchoredPopoverComponent_instances, queryVerticalPositionAnchor_fn).call(this, selector);
  if (__privateMethod(this, _AnchoredPopoverComponent_instances, isUsableExternalVerticalPositionAnchor_fn).call(this, scopedAnchor)) return scopedAnchor;
  const root = this.getRootNode();
  const anchor = root instanceof Document || root instanceof ShadowRoot ? root.querySelector(selector) : null;
  return __privateMethod(this, _AnchoredPopoverComponent_instances, isUsableExternalVerticalPositionAnchor_fn).call(this, anchor) ? anchor : null;
};
/**
 * @param {string} selector - Escaped selector for the external anchor.
 * @returns {HTMLElement | null}
 */
queryVerticalPositionAnchor_fn = function(selector) {
  const scope = this.closest("[data-anchor-scope], tr, li, section, article, form");
  if (!(scope instanceof HTMLElement)) return null;
  const anchor = scope.querySelector(selector);
  return anchor instanceof HTMLElement ? anchor : null;
};
/**
 * @param {Element | null} anchor - External anchor candidate.
 * @returns {anchor is HTMLElement}
 */
isUsableExternalVerticalPositionAnchor_fn = function(anchor) {
  if (!(anchor instanceof HTMLElement)) return false;
  const rect = anchor.getBoundingClientRect();
  return rect.width > 0 && rect.height > 0;
};
_resizeListener = new WeakMap();
if (!customElements.get("anchored-popover-component")) {
  customElements.define("anchored-popover-component", AnchoredPopoverComponent);
}
function getTriggerByAriaControls(ariaControls) {
  if (!ariaControls) return null;
  const trigger = document.querySelector('[aria-controls="'.concat(CSS.escape(ariaControls), '"]'));
  return trigger instanceof HTMLElement && trigger.isConnected ? trigger : null;
}
export {
  AnchoredPopoverComponent
};
